import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { InputContainer } from '../InputContainer';
import { InputProps, withField } from '../withField';

export interface NumberInputProps {
  placeholder?: string;

  suffix?: string;
  prefix?: string;

  min?: number;
  max?: number;
  readonly?: boolean;
  onBlur?: (value: number) => void;
}

export const NumberInput: FC<InputProps<number> & NumberInputProps> = ({
  onChange,
  onBlur,
  value = 0,
  id,
  placeholder,
  suffix,
  prefix,
  min,
  max,
  readonly,
  className,
  autofocus
}) => {
  const onTextChange = (text: string) => {
    const number = parseInt(text, 10);
    if (isNaN(number)) {
      onChange(0);
      return;
    }

    if (min !== undefined && number < min) {
      onChange(min);
      return;
    }

    if (max !== undefined && number > max) {
      onChange(max);
      return;
    }

    onChange(number);
  };

  const onTextBlur = (text: string) => {
    if (!onBlur) return;

    const number = parseInt(text, 10);
    if (isNaN(number)) {
      onBlur(0);
      return;
    }

    if (min !== undefined && number < min) {
      onBlur(min);
      return;
    }

    if (max !== undefined && number > max) {
      onBlur(max);
      return;
    }

    onBlur(number);
  };

  const innerClassName =
    'placeholder:text-secondary-std block w-full flex-1 border-0 bg-transparent p-0 text-sm placeholder:opacity-60 focus:ring-0 sm:leading-6';

  return (
    <InputContainer className={twMerge('px-3 py-2', className)}>
      {prefix && <span className="text-secondary-std mr-0.5 text-sm">{prefix}</span>}
      <input
        autoFocus={autofocus}
        value={value.toString()}
        onChange={e => onTextChange(e.target.value)}
        onBlur={e => onBlur && onTextBlur(e.target.value)}
        type="number"
        id={id}
        className={innerClassName}
        placeholder={placeholder}
        readOnly={readonly}
      />
      {suffix && <span className="text-secondary-std ml-0.5 text-sm">{suffix}</span>}
    </InputContainer>
  );
};

export const NumberField = withField<number, NumberInputProps>(NumberInput);
